import type { VNode } from 'preact';
import { useCallback } from 'preact/hooks';
import { Fade } from '@core/components/Transitions/Fade';
import { AppClipExitScreen } from '@core/components/Layout';
import { CopyToClipboardTrigger } from '@core/components/Generics/CopyToClipboardTrigger';
import { SubtitleOverlay, VideoRecorderUI } from '@core/components/Media';
import useUnlockAudio from '@core/hooks/useUnlockAudio';
import { useARExperience, AppState } from '@core/hooks/useARExperience';
import { useXRErrorGuidance } from '@core/hooks/useXRErrorGuidance';

import { AppLoadingOverlay } from './components/AppLoadingOverlay';
import { Header } from './components/Header';
import { HeroSection } from './components/HeroSection';
import { ContentSection, MapSection } from './components/LandingPage';
import { ARButton } from './components';
import { AROverlay } from './components/AROverlay';
import { useAppAnalytics } from './hooks/useAppAnalytics';

import type { Data } from '@core/content/types';

import Background from '@assets/images/hero-bg.webp';
import Logo from '@assets/images/cop-logo.webp';
import DiscoverMoreImage from '@assets/images/discover-more.webp';
import SeaQueenFigureBg from '@assets/images/sea-queen-figure-bg.png';
import SeaQueenFigureImage from '@assets/images/sea-queen-figure.webp';
import TonyJones1 from '@assets/images/tony-jones-1.webp';
import TonyJones2 from '@assets/images/tony-jones-2.webp';
import TonyJones3 from '@assets/images/tony-jones-3.webp';
import StandingFiguresPlanBg from '@assets/images/standing-figures-plan-bg.png';
import StandingFiguresPlan from '@assets/images/standing-figures-plan.webp';
import OtherWorks from '@assets/images/other-works.webp';

export interface AppProps {
  contentData?: Data[];
}

export function App(props?: AppProps): VNode {
  const contentData = props?.contentData ?? [];
  useUnlockAudio(['ejw-video', 'ejw-audio']);
  const { sendEvent } = useAppAnalytics({
    measurementId: 'G-5SGFJ0YBQB',
    debug: import.meta.env.DEV,
  });

  const checkRuntimeAppReady = useCallback(async () => {
    const { getApp } = await import('./appRegistry');
    const app = getApp();
    return !!app?.isReady();
  }, []);

  const {
    appState,
    selectedModel,
    modelReady,
    isPreparingRuntime,
    isPlaced,
    isRecording,
    setIsRecording,
    showLanding,
    xrIsChecking,
    xrIsCompatible,
    handleLaunchAR,
    handleExitAR,
  } = useARExperience({
    contentData,
    checkRuntimeAppReady,
    onLaunchClick: (model) => {
      sendEvent('ar_launch_click', {
        content_id: model.id,
        content_name: model.name,
      });
    },
    onEnteringXR: (selectedModel) => {
      sendEvent('ar_session_start', selectedModel ? { content_id: selectedModel.id } : undefined);
    },
    onExitingXR: ({ durationSeconds }, selectedModel) => {
      sendEvent(
        'ar_session_end',
        selectedModel
          ? { content_id: selectedModel.id, duration_seconds: durationSeconds }
          : { duration_seconds: durationSeconds }
      );
      if (selectedModel) {
        sendEvent('content_view', {
          content_id: selectedModel.id,
          content_name: selectedModel.name,
          duration_seconds: durationSeconds,
        });
      }
    },
  });

  const { errorMessage, showCopyLinkGuidance } = useXRErrorGuidance();
  return (
    <>
      {showLanding && (
        <div className="relative w-full min-h-[100dvh] overflow-x-hidden bg-white flex flex-col">
          <Header className="bg-[#EA2946]">
            <img src={Logo} alt="Logo" className="w-full h-auto max-w-[140px] sm:max-w-[160px] 2xl:max-w-[210px] mt-3" />
          </Header>
          <main className="flex flex-col flex-1">
            <HeroSection backgroundImage={Background}>
              <div className="flex-1 w-full flex flex-col items-start justify-center">
                <div className="flex flex-col gap-5 text-left">
                  <h1 className="text-white text-5xl sm:text-7xl 2xl:text-8xl font-bold leading-[0.9]">
                    Sea Queen and <br />
                    Standing Figure
                  </h1>
                  <h3 className="text-white text-2xl sm:text-3xl 2xl:text-4xl">
                    Sculptures by Tony Jones
                  </h3>
                </div>
              </div>
              <div className="relative w-full flex justify-center items-end flex-shrink-0 pb-10 md:pb-14 -translate-y-4 md:-translate-y-6">
                <ARButton
                  model={contentData[0]}
                  onLaunchAR={handleLaunchAR}
                  isChecking={xrIsChecking}
                  isCompatible={xrIsCompatible}
                  runtimeIsPreparing={isPreparingRuntime}
                />
                <button
                  type="button"
                  onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                  className="absolute bottom-0 md:bottom-2 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center"
                  aria-label="Scroll to next section"
                >
                  <svg
                    className="animate-scroll-indicator w-8 h-8 md:w-10 md:h-10 text-white"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M5 12 L12 19 L19 12" />
                  </svg>
                </button>
              </div>
            </HeroSection>
            <ContentSection
              id="about"
              variant="white"
            >
              <div className="flex flex-col gap-9 md:gap-10 2xl:gap-[60px]">
                <div className="flex flex-col gap-[6px] md:gap-1 2xl:gap-2">
                  <p className="text-[#EA2946] text-3xl md:text-4xl 2xl:text-5xl font-medium leading-[1]">
                    Discover More in Augmented Reality
                  </p>
                  <p className="text-[#414142] text-xl md:text-2xl 2xl:text-[28px] leading-[1.4]">
                    Unlock hidden stories and insights about this artwork.
                  </p>
                </div>

                <div className="flex flex-col gap-7 md:gap-8 2xl:gap-[45px]">
                  <ol className="list-decimal pl-6 text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                    <li>Tap <span className="font-bold">View in AR.</span></li>
                    <li>Point your camera at the artwork.</li>
                    <li>Move in to discover more about the artwork in AR.</li>
                  </ol>
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                    * AR plaques are currently being trialled by the City of Perth and may not be available for every artwork.
                  </p>
                </div>

                <img
                  src={DiscoverMoreImage}
                  alt="People viewing artwork in augmented reality"
                  className="w-full h-auto object-cover"
                />
              </div>
            </ContentSection>
            <ContentSection
              id="history"
              variant="grey"
              backgroundImage={SeaQueenFigureBg}
              backgroundPosition="left bottom"
            >
              <div className="flex flex-col gap-9 md:gap-10 2xl:gap-[60px]">
                  <p className="text-[#EA2946] text-3xl md:text-4xl 2xl:text-5xl font-medium leading-[1]">
                    Sea Queen & Standing Figure
                  </p>

                <div className="flex flex-col gap-7 md:gap-8 2xl:gap-[45px]">
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4] ">
                  A two-component artwork by acclaimed Western Australian Artist Tony Jones, <span className="italic">Sea Queen</span> and <span className="italic">Standing Figure</span> were originally commissioned by the East Perth Redevelopment Authority as part of the Claisbrook Cove and East Perth revitalisation works.
                  </p>
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                  To make this artwork the Artist recovered and restored the old riverboat <span className="italic">Sea Queen</span> which is now permanently moored here in the Cove. <span className="italic">Standing Figure</span> is made using parts of a hull and is holding a carved yacht typical of those that sailed this stretch of the Derbal Yerrigan (Swan River).
                  </p>
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                  Both components work together to symbolise and represent memories of a life on the water or around this stretch of the river.
                  </p>
                </div>

                <img
                  src={SeaQueenFigureImage}
                  alt="Sea Queen figure sculpture"
                  className="w-full h-auto object-cover"
                />
              </div>
            </ContentSection>
            <ContentSection
              id="artist"
              variant="white"
            >
              <div className="flex flex-col gap-9 md:gap-10 2xl:gap-[60px]">
                <p className="text-[#EA2946] text-3xl md:text-4xl 2xl:text-[44px] font-medium leading-[1]">
                  Tony Jones
                </p>

                <div className="flex flex-col gap-5 md:gap-6 2xl:gap-8">
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                    Tony is a titan in the West Australian arts community having served as a teacher and mentor to many over his more than 50 year career. Tony was the initial driving force behind events like Sculpture at Bathers and his extensive public art installations have had a major impact on the urban landscape in Western Australia.
                  </p>
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                    His studio is located in an industrial precinct near Fremantle and his artistic vision encompasses and combines cultural and political issues, local history, maritime and aeronautical imagery and well as themes from the personal and imagined.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4 2xl:gap-5 w-full">
                  <img
                    src={TonyJones1}
                    alt="Tony Jones artwork"
                    className="w-full"
                  />
                  <img
                    src={TonyJones2}
                    alt="Tony Jones artwork"
                    className="w-full"
                  />
                  <img
                    src={TonyJones3}
                    alt="Tony Jones artwork"
                    className="w-full"
                  />
                </div>
              </div>
            </ContentSection>
            <ContentSection
              id="artwork-details"
              variant="grey"
              backgroundImage={StandingFiguresPlanBg}
              backgroundPosition="right bottom"
            >
              <div className="flex flex-col gap-9 md:gap-10 2xl:gap-[60px]">
                <p className="text-[#EA2946] text-3xl md:text-4xl 2xl:text-[44px] font-medium leading-[1]">
                  Standing Figure and Sea Queen
                </p>

                <img
                  src={StandingFiguresPlan}
                  alt="Standing Figure and Sea Queen plan"
                  className="w-full h-auto object-cover"
                />

                <div className="flex flex-col gap-5 md:gap-6 2xl:gap-8">
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                    Standing Figure is comprised of wooden and iron components fashioned to depict a human-like figure. The iron has been painted a dark grey. In one hand the figure holds a light (to be seen by boaters at night, in addition to artistic value) and in the other carries a wooden toy boat.
                  </p>
                  <p className="text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                    Sea Queen has been fabricated from an old wooden boat now encased in fiberglass and fitted with an acrylic window. The interior is fitted with a drain to address rainwater, and inside visitors will find the bottom half of a brown, tailed creature looking through the window. This vessel has been painted red, white and blue and is displayed on a built wooden deck stained brown. On the edges of this deck are fish outlines backed with galvanised iron.
                  </p>
                </div>
              </div>
            </ContentSection>
            <ContentSection
              id="other-works"
              variant="white"
            >
              <div className="flex flex-col gap-9 md:gap-10 2xl:gap-[60px]">
                <p className="text-[#EA2946] text-3xl md:text-4xl 2xl:text-5xl font-medium leading-[1]">
                  Other works close by
                </p>

                <img
                  src={OtherWorks}
                  alt="Map of other artworks nearby"
                  className="w-full h-auto object-cover"
                />

                <ol className="list-none text-[#414142] text-lg md:text-xl 2xl:text-2xl leading-[1.4]">
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">1.</span><span>Penrose's Impossible Triangle – Brian McKay, Brian Abas and Ahmad Abas</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">2.</span><span>Sculptured Seating – Mark Cox</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">3.</span><span>Channel Markers x2 – Malcolm McGregor</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">4.</span><span>Shimmer – Jurek Wybraniec, Stephe Neille</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">5.</span><span>Concrete Spiral – Robert Finlayson</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">6.</span><span>Illa Kuri Sacred Dreaming Path – Toogarr Morrison</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">7.</span><span>Regeneration – Joanna Capelle</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">8.</span><span>Care Taker's Cottage and Memories of Mrs Shenton – Ralph Drexel</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">9.</span><span>Charnock Woman – Sandra Hill, Jenny Dawson and Miv Egan</span></li>
                  <li className="flex"><span className="shrink-0 w-6 md:w-7">10.</span><span>Stone Circle – Toogarr Morrison</span></li>
                </ol>
              </div>
            </ContentSection>
            <MapSection
              id="map"
              onViewLocation={() => {
                window.open(
                  'https://maps.app.goo.gl/G1m9fxgC4FkqjqjWA',
                  '_blank',
                );
              }}
            />
          </main>
          <footer className="h-20 sm:h-24 2xl:h-[124px] w-full flex-shrink-0 px-4 sm:px-6 lg:px-8 flex items-center justify-between bg-[#1C2429]">
            <img
              src={Logo}
              alt="City of Perth"
              className="w-full h-auto max-w-[80px] sm:max-w-[100px] 2xl:max-w-[136px]"
            />
            <div className="flex flex-wrap items-center justify-end gap-4 md:gap-5 text-sm text-white">
              <a href="https://www.perth.wa.gov.au/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="hover:text-white/70 transition-colors underline underline-offset-2">
                Terms & Conditions
              </a>
             
            </div>
          </footer>
        </div>
      )}

      {/* Loading Overlay - instant show/hide, no fade (prevents canvas flicker) */}
      {(appState === AppState.TRANSITIONING_XR ||
        (appState === AppState.AR_EXPERIENCE && !modelReady)) && (
        <div className="fixed inset-0 z-[60]">
          <AppLoadingOverlay
            message={'Loading experience..'}
          />
        </div>
      )}

      {/* AR Overlay - only when model is ready */}
      <Fade visible={appState === AppState.AR_EXPERIENCE && modelReady} duration={300}>
        <AROverlay onExit={handleExitAR} isRecording={isRecording} useLocalization />
      </Fade>

      {/* Subtitle Overlay - when in AR experience with model ready */}
      {appState === AppState.AR_EXPERIENCE && modelReady && <SubtitleOverlay />}

      {/* Video Recorder UI - only when model is placed (not during placement) */}
      <Fade visible={appState === AppState.AR_EXPERIENCE && modelReady && isPlaced} duration={300}>
        <VideoRecorderUI
          buttonPosition="bottom-center"
          shrink
          shareTitle={selectedModel?.name || 'AR Experience'}
          onRecordingRequest={() => setIsRecording(true)}
          onRecordingStart={() => setIsRecording(true)}
          onRecordingStop={() => setIsRecording(false)}
          onError={() => setIsRecording(false)}
        />
      </Fade>

      {/* App Clip Exit Screen */}
      {appState === AppState.APP_CLIP_EXITED && <AppClipExitScreen />}

      {/* XR Error Screen — shown on permission denial and on WebXR
          incompatibility (replaces the old greyed-out AR button). Mirrors
          ejc-world: an Android browser that lacks WebXR (Firefox / Opera /
          in-app webview) gets the "copy link → open in Chrome" guidance;
          everything else gets the generic message with an OK/reload button. */}
      {errorMessage && (
        <div className="fixed inset-0 z-[3000] bg-black flex flex-col min-h-[100dvh]">
          {/* Header */}
          <div className="w-full bg-[#EA2946] px-5 py-5 flex items-center justify-start">
            <img
              src={Logo}
              alt="City of Perth"
              className="h-7 w-auto"
            />
          </div>

          {/* Close — top-right below the header. Matches the video preview
              close button (54px circle, inverts on press); returns to home. */}
          <div className="w-full flex justify-end px-4 pt-4 flex-shrink-0">
            <button
              type="button"
              onClick={() => window.location.reload()}
              aria-label="Close and return to home"
              className="group flex justify-center items-center hover:opacity-80"
            >
              <svg width="54" height="54" viewBox="0 0 50 50" fill="none" className="block group-active:hidden">
                <circle cx="25" cy="25" r="25" fill="black" fillOpacity="0.5" />
                <circle cx="25" cy="25" r="23.5" stroke="white" strokeWidth="2" />
                <path d="M22.9287 25.0055L16.4062 18.4789L18.4886 16.4062L25 22.9218L31.5224 16.4062L33.5937 18.4789L27.0823 25.0055L33.5937 31.5211L31.5224 33.5937L25 27.0782L18.4886 33.5937L16.4173 31.5211L22.9287 25.0055Z" fill="white" />
              </svg>
              <svg width="54" height="54" viewBox="0 0 50 50" fill="none" className="hidden group-active:block">
                <circle cx="25" cy="25" r="25" fill="white" />
                <circle cx="25" cy="25" r="23.5" stroke="white" strokeWidth="2" />
                <path d="M22.9287 25.0055L16.4062 18.4789L18.4886 16.4062L25 22.9218L31.5224 16.4062L33.5937 18.4789L27.0823 25.0055L33.5937 31.5211L31.5224 33.5937L25 27.0782L18.4886 33.5937L16.4173 31.5211L22.9287 25.0055Z" fill="black" />
              </svg>
            </button>
          </div>

          {/* Content */}
          {showCopyLinkGuidance ? (
            <div className="flex-1 flex flex-col items-center justify-end px-5 pb-12 gap-8 text-center animate-fade-in">
              <p className="text-white text-xl short:text-lg font-light leading-[1.4] max-w-[340px]">
                To run this experience copy the link below and paste it in your Chrome browser.
              </p>
              <CopyToClipboardTrigger text={window.location.href} color="white">
                <div className="border-2 min-w-[250px] border-white/30 py-3 px-4 flex items-center justify-center tracking-[1px] hover:border-white/50 transition-colors">
                  COPY LINK
                </div>
              </CopyToClipboardTrigger>
            </div>
          ) : (
            <div className="flex-1 flex items-end justify-center px-5 pb-12">
              <div
                className="text-white text-2xl short:text-xl text-center font-light animate-fade-in"
                dangerouslySetInnerHTML={{ __html: errorMessage }}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}
